import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus } from '@phosphor-icons/react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { createProfileTableColumns } from '../../components/profile-table/profile-table-columns';
import { TransferDialog } from '../../components/transfer-dialog/transfer-dialog';
import { useDeletePotentialProfile, useGetPotentialProfiles } from '../../hooks/use-profiles';
import { DEFAULT_POTENTIAL_PROFILES_ORDER } from '../../graphql/queries';
import { PotentialProfile, PotentialProfilesPagingResult } from '../../types/profile.types';
import { PageHeader } from '@/components/core';
import { useEmployeesByUserIds } from '@/modules/shared/hr-lookups';
import { APP_PATHS } from '@/constant/app-paths';

export const ProfileListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_POTENTIAL_PROFILES_ORDER,
  });
  const [transferTarget, setTransferTarget] = useState<PotentialProfile | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PotentialProfile | null>(null);

  const { data, isLoading, error } = useGetPotentialProfiles({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere({}, columnWhere),
    order,
  });
  const result = (data as { PotentialProfiles?: PotentialProfilesPagingResult })?.PotentialProfiles;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const { mutateAsync: deletePotentialProfile, isPending: isDeleting } =
    useDeletePotentialProfile();

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await deletePotentialProfile(deleteTarget.ItemId);
    setDeleteTarget(null);
  };

  // The internal referrers' names (looked up by user id); blank until loaded.
  const { byUserId: referrers } = useEmployeesByUserIds(
    items.flatMap((profile) => profile.InternalReferralUserIds ?? [])
  );
  const referrersOf = (profile: PotentialProfile) =>
    (profile.InternalReferralUserIds ?? [])
      .map((userId) => referrers.get(userId)?.DisplayName)
      .filter(Boolean)
      .join(', ');

  const columns = createProfileTableColumns({
    t,
    referrersOf,
    onTransfer: setTransferTarget,
    onDelete: setDeleteTarget,
  });

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        title={t('CANDIDATES')}
        description={isLoading ? undefined : t('PROFILES_IN_DATABASE', { count: totalCount })}
        actions={[
          {
            label: t('ADD_PROFILE'),
            icon: <Plus className="size-5" />,
            onClick: () => navigate(APP_PATHS.CANDIDATES_NEW),
          },
        ]}
      />

      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <TransferDialog
        open={Boolean(transferTarget)}
        onOpenChange={(open) => !open && setTransferTarget(null)}
        profile={transferTarget}
      />

      <ConfirmationModal
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('DELETE_PROFILE')}
        description={t('ARE_YOU_SURE_WANT_DELETE')}
        confirmText={isDeleting ? t('DELETING') : t('DELETE')}
        preventAutoClose={isDeleting}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};
